import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Copy } from 'lucide-react'
import { api, type ProjectWithStats } from '../lib/api'
import { PageHeader } from '../components/ui'

const INSTALL_TABS = ['Expo', 'React Native CLI'] as const
type InstallTab = typeof INSTALL_TABS[number]

function getInstallSnippet(tab: InstallTab): string {
  switch (tab) {
    case 'Expo':
      return `npx expo install @feedbackkit/react-native`
    case 'React Native CLI':
      return `npm install @feedbackkit/react-native`
  }
}

function getUsageSnippet(apiKey: string): string {
  return `import { useState } from 'react'
import { Modal, View } from 'react-native'
import { FeedbackKit } from '@feedbackkit/react-native'

export function MyScreen() {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Button title="Send Feedback" onPress={() => setOpen(true)} />

      <Modal visible={open} animationType="slide" transparent>
        <View style={{ flex: 1, justifyContent: 'flex-end' }}>
          <FeedbackKit
            apiKey="${apiKey}"
            onSubmit={() => setOpen(false)}
          />
        </View>
      </Modal>
    </>
  )
}`
}

export function SDKSetup() {
  const [searchParams] = useSearchParams()
  const initialProject = searchParams.get('project') ?? ''

  const [projects, setProjects] = useState<ProjectWithStats[]>([])
  const [projectId, setProjectId] = useState(initialProject)
  const [installTab, setInstallTab] = useState<InstallTab>('Expo')
  const [copied, setCopied] = useState<string | null>(null)
  const [received, setReceived] = useState(false)
  const [totalFeedback, setTotalFeedback] = useState(0)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    api.projects.list().then(({ projects: p }) => {
      setProjects(p)
      if (!projectId && p[0]) {
        setProjectId(p[0].id)
        setTotalFeedback(p[0].feedback_count)
        if (p[0].feedback_count > 0) setReceived(true)
      }
    })
  }, [])

  useEffect(() => {
    if (!projectId) return
    if (received) return

    pollRef.current = setInterval(async () => {
      try {
        const stats = await api.feedback.stats(projectId)
        if (stats.totals.total > totalFeedback) {
          setReceived(true)
          setTotalFeedback(stats.totals.total)
          clearInterval(pollRef.current!)
        }
      } catch { /* ignore */ }
    }, 3000)

    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [projectId, received, totalFeedback])

  const activeProject = projects.find(p => p.id === projectId)
  const apiKey = activeProject?.api_key ?? 'fb_live_xxxxxxxxxxxxxxxx'

  function copy(text: string, id: string) {
    navigator.clipboard.writeText(text)
    setCopied(id)
    setTimeout(() => setCopied(null), 2000)
  }

  return (
    <div style={{ padding: '40px 48px', maxWidth: 720 }}>
      <PageHeader
        title="Install SDK"
        subtitle={activeProject ? `${activeProject.name} · ${apiKey}` : 'Select a project'}
        actions={
          <select value={projectId} onChange={e => setProjectId(e.target.value)}
            style={{ background: '#eceef0', border: 'none', color: '#191c1e', borderRadius: 8, padding: '7px 12px', fontSize: 14, fontFamily: 'Inter, sans-serif', outline: 'none', cursor: 'pointer' }}>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        }
      />

      {/* Step indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 32 }}>
        {[
          { num: 1, label: 'Install', done: true },
          { num: 2, label: 'Add to app', done: false, active: true },
          { num: 3, label: 'Verify', done: received },
        ].map(({ num, label, done, active }, i) => (
          <div key={num} style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: done && num === 3 ? '#059669' : done || active ? '#3525cd' : '#eceef0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 600,
                color: done || active ? '#fff' : '#777587',
              }}>
                {done && num !== 2 ? '✓' : num}
              </div>
              <span style={{ fontSize: 12, color: active ? '#191c1e' : '#777587', whiteSpace: 'nowrap', fontWeight: active ? 500 : 400 }}>{label}</span>
            </div>
            {i < 2 && (
              <div style={{ width: 80, height: 2, background: '#eceef0', margin: '0 12px', marginBottom: 20, borderRadius: 2 }} />
            )}
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Step 1 - Install */}
        <StepCard num={1} title="Install the package" done>
          <div style={{ display: 'flex', gap: 0, marginBottom: 16, background: '#f2f4f6', borderRadius: 8, padding: 4, width: 'fit-content' }}>
            {INSTALL_TABS.map(t => (
              <button key={t} onClick={() => setInstallTab(t)}
                style={{
                  padding: '5px 14px', borderRadius: 6, border: 'none',
                  background: installTab === t ? '#ffffff' : 'transparent',
                  color: installTab === t ? '#3525cd' : '#777587',
                  cursor: 'pointer', fontSize: 13, fontFamily: 'Inter, sans-serif',
                  fontWeight: installTab === t ? 600 : 400,
                  boxShadow: installTab === t ? '0 1px 4px rgba(25,28,30,0.08)' : 'none',
                  transition: 'all 0.15s',
                }}>
                {t}
              </button>
            ))}
          </div>
          <div style={{ position: 'relative' }}>
            <CodeBlock code={getInstallSnippet(installTab)} />
            <button onClick={() => copy(getInstallSnippet(installTab), 'install')}
              style={{ position: 'absolute', top: 10, right: 10, background: '#eceef0', border: 'none', color: copied === 'install' ? '#059669' : '#464555', cursor: 'pointer', borderRadius: 6, padding: '4px 10px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'Inter, sans-serif', transition: 'color 0.15s' }}>
              {copied === 'install' ? '✓ Copied' : <><Copy size={12} /> Copy</>}
            </button>
          </div>
        </StepCard>

        {/* Step 2 - Usage */}
        <StepCard num={2} title="Add to your app" active>
          <div style={{ position: 'relative' }}>
            <CodeBlock code={getUsageSnippet(apiKey)} />
            <button onClick={() => copy(getUsageSnippet(apiKey), 'usage')}
              style={{ position: 'absolute', top: 10, right: 10, background: '#eceef0', border: 'none', color: copied === 'usage' ? '#059669' : '#464555', cursor: 'pointer', borderRadius: 6, padding: '4px 10px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'Inter, sans-serif', transition: 'color 0.15s' }}>
              {copied === 'usage' ? '✓ Copied' : <><Copy size={12} /> Copy</>}
            </button>
          </div>
          <div style={{ marginTop: 12, fontSize: 13, color: '#464555', background: 'rgba(53,37,205,0.05)', padding: '10px 14px', borderRadius: 8, border: 'none' }}>
            💡 <code style={{ color: '#3525cd' }}>&lt;FeedbackKit&gt;</code> is just a form component — render it anywhere: modal, bottom sheet, page, drawer. You control the container.
          </div>
        </StepCard>

        {/* Step 3 - API Key */}
        <StepCard num={3} title="Your API key" done>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <code style={{
              flex: 1, background: '#f2f4f6', border: 'none',
              borderRadius: 8, padding: '10px 14px', fontSize: 13,
              color: '#3525cd', fontFamily: "'Fira Code', 'Courier New', monospace",
              wordBreak: 'break-all',
            }}>{apiKey}</code>
            <button onClick={() => copy(apiKey, 'apikey')}
              style={{ flexShrink: 0, background: '#eceef0', border: 'none', color: copied === 'apikey' ? '#059669' : '#464555', cursor: 'pointer', borderRadius: 8, padding: '8px 12px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'Inter, sans-serif', transition: 'color 0.15s' }}>
              {copied === 'apikey' ? '✓ Copied' : <><Copy size={12} /> Copy</>}
            </button>
          </div>
          <div style={{ marginTop: 10, fontSize: 13, color: '#777587' }}>
            Keep this key in your app — it identifies your project and loads your form configuration.
          </div>
        </StepCard>

        {/* Step 4 - Verify */}
        <StepCard num={4} title="Verify connection" done={received}>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            {received ? (
              <>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: '#059669', fontFamily: 'Manrope, sans-serif' }}>
                  First feedback received!
                </div>
                <div style={{ color: '#777587', fontSize: 14 }}>
                  Your SDK is working. Head to Feedback to see it.
                </div>
              </>
            ) : (
              <>
                <div style={{ position: 'relative', width: 56, height: 56, margin: '0 auto 16px' }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%',
                    border: '3px solid #eceef0', borderTopColor: '#3525cd',
                    animation: 'spin 1s linear infinite',
                  }} />
                  <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
                </div>
                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#191c1e', fontFamily: 'Manrope, sans-serif' }}>Waiting for first feedback…</div>
                <div style={{ color: '#777587', fontSize: 14 }}>
                  Run your app, open the feedback form and submit a test message. This page updates automatically.
                </div>
              </>
            )}
          </div>
        </StepCard>
      </div>

      {/* Help cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 28 }}>
        {[
          { icon: '📖', title: 'Read the docs', sub: 'docs.feedbackkit.dev' },
          { icon: '💬', title: 'Join Discord', sub: '340 members' },
          { icon: '🎥', title: 'Watch setup video', sub: '2 min walkthrough' },
        ].map(({ icon, title, sub }) => (
          <div key={title} style={{
            background: '#ffffff', borderRadius: 12, padding: '16px 18px',
            boxShadow: '0 2px 8px rgba(25,28,30,0.06)', cursor: 'pointer',
            transition: 'box-shadow 0.15s',
          }}>
            <div style={{ fontSize: 24, marginBottom: 10 }}>{icon}</div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3, color: '#191c1e' }}>{title}</div>
            <div style={{ fontSize: 12, color: '#777587' }}>{sub}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function StepCard({ num, title, children, done, active, actions }: {
  num: number; title: string; children?: React.ReactNode
  done?: boolean; active?: boolean; actions?: React.ReactNode
}) {
  return (
    <div style={{
      background: '#ffffff',
      borderRadius: 12,
      overflow: 'hidden',
      boxShadow: active
        ? '0 0 0 2px #3525cd, 0 4px 16px rgba(53,37,205,0.08)'
        : done
          ? '0 0 0 1.5px #c8f0d8, 0 2px 8px rgba(25,28,30,0.04)'
          : '0 2px 8px rgba(25,28,30,0.06)',
      opacity: !done && !active ? 0.6 : 1,
    }}>
      <div style={{
        padding: '14px 20px',
        background: active ? 'rgba(53,37,205,0.04)' : done ? 'rgba(5,150,105,0.04)' : '#f7f9fb',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
            background: done ? '#059669' : active ? '#3525cd' : '#eceef0',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 600,
            color: done || active ? '#fff' : '#777587',
          }}>
            {done ? '✓' : num}
          </div>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#191c1e', fontFamily: 'Manrope, sans-serif' }}>{title}</span>
        </div>
        {actions}
      </div>
      {children && <div style={{ padding: 20 }}>{children}</div>}
    </div>
  )
}

function CodeBlock({ code }: { code: string }) {
  return (
    <pre style={{
      background: '#f2f4f6', borderRadius: 8, padding: '14px 16px',
      fontSize: 13, color: '#191c1e', fontFamily: "'Fira Code', 'Courier New', monospace",
      overflowX: 'auto', margin: 0, lineHeight: 1.7,
    }}>
      {code}
    </pre>
  )
}
